import { DOCUMENT_PACK_SIZES, type DocumentPackSize } from '@lark-automadoc/shared-types'

export type Currency = 'IDR' | 'USD' | 'CAD' | 'EUR'
export type BillingCycle = 'monthly' | 'quarterly' | 'yearly'
export type PlanType = 'free' | 'trial' | 'pro'

/** The two plans sold per person. Pro is not one of them: it covers the company. */
export type SeatPlan = 'starter' | 'business'

/**
 * The cycles a seat can be bought on, and the two positions of the pricing
 * page's switch.
 *
 * A subset of BillingCycle on purpose, so the switch's value can be handed to
 * a Pro checkout unchanged. Quarterly is absent because it is Pro-only and not
 * a discount — it is quoted on the Pro card, never offered as a third position
 * that would leave the seat cards with nothing to show.
 */
export type SeatCycle = Exclude<BillingCycle, 'quarterly'>

/** Every column of the plan comparison, in the order it is shown. */
export type PlanColumn = 'free' | SeatPlan | 'pro'

/**
 * The optional products an environment sells.
 *
 * Seat and pack prices are created per Paddle catalog, and each price id is
 * blank until it exists there — the same arrangement as quarterly Pro. A
 * product nobody can check out must not be advertised, so the pricing
 * components show only what they are told is on sale, and are told nothing by
 * default. This package cannot find out for itself: it has no build config of
 * its own, so each app passes what its environment carries.
 */
export interface PricingProducts {
  /** Cycles each seat plan can be bought on. A plan with none is not shown. */
  seats?: Partial<Record<SeatPlan, readonly SeatCycle[]>>
  /** Pack sizes on sale. Empty or absent hides the packs row. */
  packs?: readonly number[]
}

/**
 * Read what is on sale from an app's Paddle price ids.
 *
 * Takes the env object as an argument rather than reading `import.meta.env`
 * itself, for the reason given on PricingProducts. The variable names are the
 * ones `scripts/paddle-catalog.mjs` prints, so the portal (which needs the ids
 * to open a checkout) and the marketing site (which only mirrors them, read as
 * booleans, so it never advertises what the portal cannot sell) map them the
 * same way. A blank or missing id simply leaves that product off.
 */
export function pricingProductsFromPriceIds(env: Record<string, unknown>): PricingProducts {
  const has = (name: string) => typeof env[name] === 'string' && (env[name] as string).trim() !== ''
  const cycles = (plan: 'STARTER' | 'BUSINESS'): SeatCycle[] => [
    ...(has(`VITE_PADDLE_PRICE_SEAT_${plan}_MONTHLY`) ? ['monthly' as const] : []),
    ...(has(`VITE_PADDLE_PRICE_SEAT_${plan}_ANNUAL`) ? ['yearly' as const] : []),
  ]
  return {
    seats: { starter: cycles('STARTER'), business: cycles('BUSINESS') },
    packs: documentPacksUSD.map(pack => pack.size).filter(size => has(`VITE_PADDLE_PRICE_PACK_${size}`)),
  }
}

/** Whether any seat plan or pack is on sale — the FAQ about them shows only then. */
export function sellsSeatsOrPacks(products: PricingProducts): boolean {
  return (products.seats?.starter?.length ?? 0) > 0
    || (products.seats?.business?.length ?? 0) > 0
    || (products.packs?.length ?? 0) > 0
}

export interface SeatPrice {
  /** Charged per seat each month on the pay-monthly cycle. */
  monthly: string
  /**
   * Charged per seat each year, as a total.
   *
   * A total rather than a per-month figure, unlike Pro's headline: the annual
   * seat price is ten monthly payments for twelve months, and "2 months free"
   * is only checkable by a reader who can see both numbers as charged.
   */
  yearly: string
}

export interface PackPrice {
  /** Documents in the pack. */
  size: number
  /** One-time price. */
  price: string
  /** Price divided by size — shown so the three sizes can be compared. */
  perDocument: string
}

export interface PricingTier {
  free: string
  pro: {
    /** Charged each month on the pay-monthly cycle. */
    monthly: string
    /** Annual total divided by twelve — the headline figure everywhere. */
    annualPerMonth: string
    /**
     * Charged per quarter, as a total.
     *
     * A total rather than a per-month figure because quarterly costs the same
     * per month as monthly does — it buys fewer charges, not a lower rate.
     * Quoting "$95/month billed quarterly" next to "$95 billed monthly" states
     * the same number twice and reads like a mistake.
     *
     * The annual total ($948) is deliberately absent: nothing renders it, and
     * a figure nothing renders goes stale the first time a price moves.
     */
    quarterly: string
  }
  seats: Record<SeatPlan, SeatPrice>
  /** Smallest first. */
  packs: PackPrice[]
}

/**
 * What is charged, in USD. Keep in sync with the Paddle catalog
 * (`scripts/paddle-catalog.mjs` holds the same amounts in cents).
 *
 * Plain numbers rather than display strings because the portal multiplies them
 * by a quantity, and because every other currency below is derived from them.
 */
export const seatPricingUSD: Record<SeatPlan, { monthly: number; yearly: number }> = {
  starter: { monthly: 18, yearly: 180 },
  business: { monthly: 36, yearly: 360 },
}

/**
 * Document pack prices. Keyed by the sizes the API sells
 * (`DOCUMENT_PACK_SIZES`), so a size added there fails the typecheck here
 * until it has a price, instead of being on sale with nothing to quote.
 */
const PACK_PRICE_USD: Record<DocumentPackSize, number> = {
  1000: 79,
  5000: 269,
  50000: 1079,
}

/** Document packs — one-time purchases, smallest first. */
export const documentPacksUSD = [...DOCUMENT_PACK_SIZES]
  .sort((a, b) => a - b)
  .map(size => ({ size, price: PACK_PRICE_USD[size] }))

export type PackSize = DocumentPackSize

/**
 * A year of seats is charged as this many months ("2 months free").
 *
 * The display currencies apply it to their own rounded monthly figure rather
 * than converting the USD annual price, so the claim holds exactly in every
 * currency a visitor can switch to.
 */
export const SEAT_ANNUAL_MONTHS_CHARGED = 10

// Pack validity, the Business automation allowance and the daily free tests
// (approval tests and design previews) are quoted on these pages but enforced
// by the API, so they
// are read from shared-types (PACK_VALIDITY_MONTHS,
// AUTOMATION_DOCS_PER_BUSINESS_SEAT, FREE_TESTS_PER_DAY) rather than
// mirrored here. Only what is display-only — prices — lives in this file.

/**
 * How a USD price becomes a display figure in another currency.
 *
 * `perUsd` is that currency's Pro monthly price over Pro's $95, so a seat or a
 * pack sits at the same ratio to Pro in every currency — a visitor comparing
 * "three Business seats" with Pro reaches the same answer whichever currency
 * they picked. `unit` is the precision the existing figures are quoted to:
 * whole dollars and euros, thousands of rupiah.
 *
 * Pro's own annual figures are *not* reproduced by this (each currency carries
 * its own annual discount), which is why they stay literal in the table below.
 */
const DISPLAY: Record<Currency, { symbol: string; perUsd: number; unit: number }> = {
  USD: { symbol: '$', perUsd: 1, unit: 1 },
  CAD: { symbol: 'C$', perUsd: 129 / 95, unit: 1 },
  EUR: { symbol: '€', perUsd: 79 / 95, unit: 1 },
  IDR: { symbol: 'Rp ', perUsd: 1_600_000 / 95, unit: 1000 },
}

/**
 * Grouping is fixed to the `1,600,000` style the price table has always used,
 * in every interface language: these strings sit next to the literal ones
 * below, and two conventions in one card would read as two different numbers.
 */
export function formatPricingNumber(amount: number, decimals = 0): string {
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

function toDisplay(currency: Currency, usd: number): number {
  const { perUsd, unit } = DISPLAY[currency]
  return Math.round((usd * perUsd) / unit) * unit
}

function money(currency: Currency, amount: number, decimals = 0): string {
  return `${DISPLAY[currency].symbol}${formatPricingNumber(amount, decimals)}`
}

function seatPrice(currency: Currency, plan: SeatPlan): SeatPrice {
  const monthly = toDisplay(currency, seatPricingUSD[plan].monthly)
  return {
    monthly: money(currency, monthly),
    yearly: money(currency, monthly * SEAT_ANNUAL_MONTHS_CHARGED),
  }
}

function packPrices(currency: Currency): PackPrice[] {
  // A rupiah has no cents worth quoting; the others need three decimals or the
  // largest pack rounds to the same figure as the middle one.
  const perDocumentDecimals = currency === 'IDR' ? 0 : 3
  return documentPacksUSD.map(({ size, price }) => {
    const shown = toDisplay(currency, price)
    return {
      size,
      price: money(currency, shown),
      perDocument: money(currency, shown / size, perDocumentDecimals),
    }
  })
}

function seatsAndPacks(currency: Currency): Pick<PricingTier, 'seats' | 'packs'> {
  return {
    seats: { starter: seatPrice(currency, 'starter'), business: seatPrice(currency, 'business') },
    packs: packPrices(currency),
  }
}

/**
 * Plan prices per currency.
 *
 * Quarterly is a billing frequency, not a cheaper tier: $285 a quarter is the
 * same $95/month as paying monthly, and it exists so a customer can commit for
 * three months without committing for a year. Annual remains the only discount.
 * Non-USD quarterly figures are simply three times that currency's monthly
 * rate. Only the USD column is ever charged — Paddle checkout is USD-only, and both
 * the portal and the add-on blocks read `pricing.USD` directly — so the rest
 * are display figures for the marketing page's currency toggle, same as they
 * have always been for annual.
 *
 * Seats and packs follow the same rule: USD is what is charged, and the other
 * three are derived from it by `DISPLAY` above rather than typed in, so a
 * price change is made in one place.
 */
export const pricing: Record<Currency, PricingTier> = {
  IDR: {
    free: 'Rp 0',
    pro: {
      monthly: 'Rp 1,600,000',
      annualPerMonth: 'Rp 1,249,000',
      quarterly: 'Rp 4,800,000'
    },
    ...seatsAndPacks('IDR')
  },
  USD: {
    free: '$0',
    pro: {
      monthly: '$95',
      annualPerMonth: '$79',
      quarterly: '$285'
    },
    ...seatsAndPacks('USD')
  },
  CAD: {
    free: 'C$0',
    pro: {
      monthly: 'C$129',
      annualPerMonth: 'C$109',
      quarterly: 'C$387'
    },
    ...seatsAndPacks('CAD')
  },
  EUR: {
    free: '€0',
    pro: {
      monthly: '€79',
      annualPerMonth: '€69',
      quarterly: '€237'
    },
    ...seatsAndPacks('EUR')
  }
}

/**
 * Self-serve add-on prices, in USD.
 *
 * Paddle checkout is USD-only for now, so unlike the plan table above these are
 * plain numbers (the quantity picker multiplies them) rather than per-currency
 * display strings. Keep in sync with the Paddle catalog.
 *
 * The $29 Generation Top-Up used to be listed here. It is retired in favour of
 * document packs and is deliberately not kept as a figure "for reference": the
 * API still honours its price id for an old payment link, but nothing may
 * quote it as something for sale.
 */
export const addonPricingUSD = {
  /** Extra Templates — per template, per month */
  extraTemplatePerMonth: 3,
}

/**
 * i18n keys for free plan features.
 *
 * What every platform gets comes first; the one Lark-only line (Lark Approval
 * documents) is last and says it is Lark's, so a Microsoft or Google reader
 * does not take it for something missing on their side.
 */
export const freeFeatureKeys = [
  'pricingFreeDocuments',
  'pricingOneTemplate',
  'pricingUnlimitedUsers',
  'pricingWordAndPdf',
  'pricingESignature',
  // Every plan (decision §11.10), counted against the monthly allowance.
  'pricingDocumentApi',
  'pricingBulkGeneration',
  'pricingDynamicImages',
  'pricingImageCompression',
  'pricingOneDayRetention',
  'pricingFooterLine',
  'pricingSharedResources',
  'pricingEmailSupport',
  'pricingApprovalDocuments'
] as const

/**
 * What a Starter seat adds, for the person who holds it.
 *
 * The last two lines are about the company, not the seat holder, and say so —
 * a seat is bought for one person, and the first question it raises is what
 * everyone else gets.
 */
export const starterFeatureKeys = [
  'pricingSeatUnlimitedByHand',
  'pricingSeatNoWait',
  'pricingSeatNoFooter',
  'pricingAutoSave',
  'pricingSeatCompanyTemplates',
  'pricingSeatColleagues'
] as const

// i18n keys for what a Business seat adds on top of Starter
export const businessFeatureKeys = [
  'pricingHttpTrigger',
  'pricingWebhookCallback',
  'pricingStatusField',
  'pricingAutomationAllowance'
] as const

/**
 * i18n keys for pro plan features.
 *
 * Spelled out rather than "everything in Business": the seat cards are only on
 * the page where seats are sold, and this list has to stand on its own beside
 * Free where they are not.
 */
export const proFeatureKeys = [
  'pricingProEveryone',
  'pricingUnlimitedGeneration',
  'pricingAutoSave',
  'pricingProAutomation',
  'pricingProNoWaitNoFooter',
  'pricingTenTemplates',
  'pricingSevenDayRetention',
  'pricingPrioritySupport'
] as const

/**
 * A cell of the comparison table: included, not included, an i18n key for the
 * rows where the honest answer is a quantity or a condition, or a bare figure
 * (named as the components name it) where the answer is just a number.
 */
export type ComparisonCell = boolean | string | { figure: string }

export interface ComparisonRow {
  /** i18n key of the capability. */
  label: string
  cells: Record<PlanColumn, ComparisonCell>
}

/**
 * The plan comparison: rows are capabilities, columns are plans.
 *
 * Only rows whose answer is decided for all four plans are listed — a table
 * cell is a commitment, and an empty or guessed one is worse than no row.
 * Retention and priority support are the last two because they are what Pro
 * has that a seat does not (decided 2026-10-16): a single person's Pro would
 * otherwise read as a $95 Starter seat.
 *
 * The seat columns describe the person who holds the seat. The table says so
 * once, underneath, rather than in every cell.
 */
export const comparisonRows: ComparisonRow[] = [
  {
    // A plan is the company's, not a platform's: the same plan covers Microsoft
    // 365, Google Workspace, Lark and the API. Worded without naming them, so
    // the row stays true whichever platforms an environment has opened.
    label: 'pricingCompareAllPlatforms',
    cells: { free: true, starter: true, business: true, pro: true }
  },
  {
    label: 'pricingCompareCovers',
    cells: {
      free: 'pricingCompareWholeCompany',
      starter: 'pricingCompareOnePerson',
      business: 'pricingCompareOnePerson',
      pro: 'pricingCompareWholeCompany'
    }
  },
  {
    label: 'pricingCompareByHand',
    cells: {
      free: 'pricingCompareFreeAllowance',
      starter: 'pricingCompareUnlimited',
      business: 'pricingCompareUnlimited',
      pro: 'pricingCompareUnlimited'
    }
  },
  {
    label: 'pricingCompareAutomated',
    cells: {
      free: false,
      starter: false,
      business: 'pricingCompareAutomationAllowance',
      pro: 'pricingCompareUnlimited'
    }
  },
  {
    label: 'pricingCompareTemplates',
    cells: {
      free: { figure: 'freeTemplates' },
      starter: { figure: 'templates' },
      business: { figure: 'templates' },
      pro: { figure: 'templates' }
    }
  },
  {
    label: 'pricingCompareWordPdf',
    cells: { free: true, starter: true, business: true, pro: true }
  },
  {
    label: 'pricingCompareNoWait',
    cells: { free: 'pricingCompareFreeWait', starter: true, business: true, pro: true }
  },
  {
    label: 'pricingCompareNoFooter',
    cells: { free: false, starter: true, business: true, pro: true }
  },
  {
    label: 'pricingCompareSaveToField',
    cells: { free: false, starter: true, business: true, pro: true }
  },
  {
    label: 'pricingCompareStatusField',
    cells: { free: false, starter: false, business: true, pro: true }
  },
  {
    label: 'pricingCompareHttpTrigger',
    cells: { free: false, starter: false, business: true, pro: true }
  },
  {
    label: 'pricingCompareWebhook',
    cells: { free: false, starter: false, business: true, pro: true }
  },
  {
    // On every plan (identity design decision §11.10, documentApiAuthMiddleware):
    // metered by resolveCharge() — the Business allowance, then the free monthly
    // allowance, then packs. The HTTP trigger above keeps its Business/Pro gate.
    label: 'pricingCompareDocumentApi',
    cells: { free: true, starter: true, business: true, pro: true }
  },
  {
    // On every plan: sending for signature costs nothing beyond the document.
    label: 'pricingCompareESignature',
    cells: { free: true, starter: true, business: true, pro: true }
  },
  {
    // effectiveGenerationRetentionDays(): the tenant's own setting (1 day for a
    // new one), raised to PRO_GENERATION_RETENTION_DAYS while Pro is in force.
    // Company-wide, so a seat leaves it where it is.
    label: 'pricingCompareRetention',
    cells: {
      free: 'pricingCompareOneDay',
      starter: 'pricingCompareOneDay',
      business: 'pricingCompareOneDay',
      pro: 'pricingCompareSevenDays'
    }
  },
  {
    label: 'pricingComparePrioritySupport',
    cells: { free: false, starter: false, business: false, pro: true }
  },
  {
    // Lark's alone, so last, and labelled as Lark's. Approval documents are
    // automated ones, yet available on Free: the cells say where they are
    // counted, so "no automated documents included" above is not read as
    // "no approval documents".
    label: 'pricingCompareApproval',
    cells: {
      free: 'pricingCompareFromAllowance',
      starter: 'pricingCompareFromAllowance',
      business: 'pricingCompareInAutomation',
      pro: 'pricingCompareUnlimited'
    }
  }
]

/**
 * Questions answered under the pricing table, as i18n key stems: each has a
 * `…Q` and an `…A` entry in the locale files.
 */
export const pricingFaqKeys = [
  'pricingFaqSeat',
  'pricingFaqWhoNeedsSeat',
  // What 'automated' means: the Business allowance and the comparison rows use the word.
  'pricingFaqAutomated',
  'pricingFaqColleagues',
  'pricingFaqPack',
  'pricingFaqPackExpiry',
  'pricingFaqFooter',
  // Lark Approval only: last, like its rows in the plan lists.
  'pricingFaqApprovals'
] as const
